import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import * as inventoryService from "../../services/inventoryService";
import { getRole } from "../../components/Apicall";
import { formatDecimal } from "../../utils/numberFormat";
import PartsPanel from "./PartsPanel";

/**
 * "Inventory / Parts Used" picker for a brand-new booking, staged locally
 * (no bookingId exists yet - the row is only created later, in Checkout).
 * Deliberately a separate, co-located sibling to PartsUsedSection.jsx rather
 * than a mode flag on it: that component self-fetches and mutates the
 * backend immediately, this one is a fully controlled component - the
 * parent (Bookings.jsx) owns the `parts` array (as priceSection rows tagged
 * isPart) so it stays the single source of truth for the price breakdown,
 * exactly like the existing "isCustom" ad-hoc line-item rows.
 *
 * Cost fields and price override are role=3 only, matching PartsUsedSection.
 */
function StagedPartsSection({ parts, currency = "", onAdd, onRemove }) {
  const isAdmin = getRole() === "3";
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [overridePrice, setOverridePrice] = useState("");

  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      inventoryService
        .getItems({ search, is_active: 1, limit: 10 })
        .then((res) => setResults(res?.data || []));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const subtotal = parts.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const handleAdd = () => {
    if (!selectedItem || !quantity) return;
    const unitPrice =
      isAdmin && overridePrice !== "" ? Number(overridePrice) : Number(selectedItem.selling_price || 0);
    onAdd(selectedItem, Number(quantity), unitPrice);
    setSelectedItem(null);
    setSearch("");
    setQuantity(1);
    setOverridePrice("");
  };

  return (
    <PartsPanel currency={currency} total={subtotal} rows={parts.map((p) => ({
      id: p.id,
      name: p.name,
      quantity: p.quantity,
      unit: p.unit_of_measure,
      unitPrice: p.unit_price,
      lineTotal: p.amount,
      onRemove: () => onRemove(p.id)
    }))} emptyText="No parts added yet.">
      <div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Control
            style={{ maxWidth: 220 }}
            size="sm"
            placeholder="Search item by name/SKU…"
            value={selectedItem ? selectedItem.name : search}
            onChange={(e) => {
              setSelectedItem(null);
              setSearch(e.target.value);
            }}
          />
          <Form.Control
            type="number"
            size="sm"
            style={{ maxWidth: 90 }}
            min="0.001"
            step="0.001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          {isAdmin && selectedItem && (
            <div className="d-flex align-items-center" style={{ fontSize: 13, color: "#374151" }}>
              Unit price: <strong className="ms-1">{currency} {formatDecimal(selectedItem.selling_price)}</strong>
            </div>
          )}
          {isAdmin && (
            <Form.Control
              type="number"
              size="sm"
              style={{ maxWidth: 110 }}
              placeholder="Price override"
              step="0.01"
              value={overridePrice}
              onChange={(e) => setOverridePrice(e.target.value)}
            />
          )}
          <Button
            size="sm"
            disabled={!selectedItem || !quantity}
            onClick={handleAdd}
            style={{ background: "var(--button-color)", border: "none", color: "#fff" }}
          >
            + Add
          </Button>
        </div>
        {selectedItem && Number(quantity) > Number(selectedItem.current_qty) && (
          <div className="mt-1" style={{ fontSize: 12, color: "#D97706" }}>
            ⚠ Only {selectedItem.current_qty} {selectedItem.unit_of_measure} in stock — adding {quantity} will take
            stock negative.
          </div>
        )}
        {results.length > 0 && !selectedItem && (
          <div className="border rounded mt-1" style={{ maxWidth: 320, background: "#fff", position: "relative", zIndex: 5 }}>
            {results.map((item) => (
              <div
                key={item.id}
                className="px-2 py-1"
                style={{ cursor: "pointer", fontSize: 13 }}
                onClick={() => {
                  setSelectedItem(item);
                  setResults([]);
                }}
              >
                {item.name} ({item.sku}) — {item.current_qty} {item.unit_of_measure} in stock
              </div>
            ))}
          </div>
        )}
      </div>
    </PartsPanel>
  );
}

export default StagedPartsSection;
